import type { CompanyRow, GlobalPurchaseSettings } from "./bulkBuyTypes";
import {
  CodRequirement,
  EACSource,
  Ecolabel,
  isBuyTechnology,
  PurchaseMode,
  Support,
} from "./bulkBuyTypes";

/**
 * Shape of the /analyze-document extraction payload. Untrusted: every field
 * may be missing or hold a value outside our enums.
 */
export interface AiExtractedSettings {
  purchase_mode?: string;
  eac_source?: string;
  technology?: string;
  ecolabel?: string;
  support?: string;
  cod_requirement?: string;
  vintage?: string;
  custom_requirement?: string;
  hourly_matching?: boolean;
  monthly_matching?: boolean;
}

export interface AiExtractedCompany {
  country?: string;
  volume?: number;
}

export interface AiAnalysisPayload {
  companies?: AiExtractedCompany[];
  global_settings?: AiExtractedSettings;
}

const asEnumMember = <T extends string>(
  enumObj: Record<string, T>,
  value: string
): T | undefined =>
  (Object.values(enumObj) as string[]).includes(value)
    ? (value as T)
    : undefined;

/**
 * Applies extracted global settings on top of the current ones. A field is
 * only applied when the extractor produced a valid value for it; junk values
 * leave the current setting untouched.
 */
export interface MergeAiGlobalSettingsOptions {
  /** NOTE: true once the user explicitly picked a sourcing card; extraction must not override it. */
  preserveUserPurchaseMode?: boolean;
}

export function mergeAiGlobalSettings(
  current: GlobalPurchaseSettings,
  extracted: AiExtractedSettings | undefined,
  options: MergeAiGlobalSettingsOptions = {}
): GlobalPurchaseSettings {
  if (!extracted) return current;

  const next = { ...current };

  if (extracted.purchase_mode && !options.preserveUserPurchaseMode) {
    // The redesigned picker drops ADVANCED; an extracted ADVANCED maps to
    // OTHER so the request routes to a manual quote.
    const mode =
      extracted.purchase_mode === "ADVANCED"
        ? PurchaseMode.OTHER
        : extracted.purchase_mode;
    next.purchase_mode = asEnumMember(PurchaseMode, mode) ?? next.purchase_mode;
  }
  if (extracted.eac_source) {
    next.eac_source =
      asEnumMember(EACSource, extracted.eac_source) ?? next.eac_source;
  }
  if (extracted.technology && isBuyTechnology(extracted.technology)) {
    next.technology = extracted.technology;
  }
  if (extracted.ecolabel) {
    next.ecolabel = asEnumMember(Ecolabel, extracted.ecolabel) ?? next.ecolabel;
  }
  if (extracted.support) {
    next.support = asEnumMember(Support, extracted.support) ?? next.support;
  }
  if (extracted.cod_requirement) {
    next.cod_requirement =
      asEnumMember(CodRequirement, extracted.cod_requirement) ??
      next.cod_requirement;
  }
  if (extracted.vintage) {
    next.vintage = extracted.vintage;
  }
  // NOTE: an extracted custom requirement must not ride along with a non-OTHER
  // mode (e.g. a preserved user pick) — the backend nulls it for those modes,
  // and the review modal would still display it.
  if (
    extracted.custom_requirement &&
    next.purchase_mode === PurchaseMode.OTHER
  ) {
    next.custom_requirement = extracted.custom_requirement;
  }
  if (extracted.hourly_matching !== undefined) {
    next.hourly_matching = extracted.hourly_matching;
  }
  if (extracted.monthly_matching !== undefined) {
    next.monthly_matching = extracted.monthly_matching;
  }

  return next;
}

/** Maps extracted companies to beneficiary rows, pre-marked touched so validation shows what's missing. */
export function mapAiCompanies(companies: AiExtractedCompany[]): CompanyRow[] {
  return companies.map((company) => ({
    country: company.country || "",
    eac_type: "",
    // An unknown volume stays 0 so the row is flagged invalid instead of silently ordering 1 MWh.
    volume: company.volume ?? 0,
    pricing_state: { status: "idle" },
    touched: true,
  }));
}
